import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { listingService, userService, agencyService, storageService } from '../lib/databaseService';
import { 
  ArrowLeft, Edit3, Trash2, Plus, Building2, ListChecks, Image, MapPin, 
  Phone, DollarSign, Users, Wallet, Eye, Upload, X, Camera
} from 'lucide-react';

export default function AgencyOwnerCabinet({ user, agencies = [], onRefreshListings }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [allListings, setAllListings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [currentAgency, setCurrentAgency] = useState(null);

  // İstifadəçinin agentliyini tap
  const userAgency = agencies.find(a => a.id === user?.agencyId) || null;
  
  // Agentlik məlumatlarını yüklə
  useEffect(() => {
    const loadAgency = async () => {
      if (user?.agencyId) {
        try {
          const agency = await agencyService.getById(user.agencyId);
          setCurrentAgency(agency);
        } catch (error) {
          console.error('Agentlik məlumatlarını yükləmə xətası:', error);
        }
      }
    };
    loadAgency();
  }, [user?.agencyId]);

  // Loqo fayl seçildikdə
  const handleLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Yalnız şəkil fayllarına icazə ver
    if (!file.type.startsWith('image/')) {
      toast.error('Yalnız şəkil faylları yüklənə bilər');
      return;
    }

    // Fayl ölçüsü yoxlaması (maksimum 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Şəkil ölçüsü 5MB-dan çox ola bilməz');
      return;
    }

    setLogoFile(file);
    
    // Preview yarat
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Loqo yükləmə
  const handleLogoUpload = async () => {
    if (!logoFile || !user?.agencyId) {
      toast.error('Zəhmət olmasa şəkil seçin');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const updatedAgency = await agencyService.updateLogo(user.agencyId, logoFile);
      setCurrentAgency(updatedAgency);
      
      // Loqo yeniləndikdə agencies list-ini yeniləmək lazımdır
      // Burada sadəcə success mesajı göstəririk
      toast.success('Loqo uğurla yeniləndi');
      setLogoModalOpen(false);
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      console.error('Loqo yükləmə xətası:', error);
      toast.error('Loqo yüklənə bilmədi. Zəhmət olmasa yenidən cəhd edin.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Loqo modalını bağlamaq
  const handleCloseLogoModal = () => {
    setLogoModalOpen(false);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const loadData = useCallback(async () => {
    if (!user?.agencyId) return;
    
    try {
      setLoading(true);
      
      // Agentliyə aid bütün elanları gətir
      const listings = await listingService.getByAgencyId(user.agencyId);
      setAllListings(listings);
      
      // Agentliyə aid bütün əmlakçıları gətir (bu funksiya əlavə edilməlidir)
      // Hələlik boş saxlayırıq
      
    } catch (error) {
      console.error('Məlumatları yükləmə xətası:', error);
      toast.error('Məlumatları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  }, [user?.agencyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Düzəliş modalını aç
  const openEditModal = (listing) => {
    setSelectedListing(listing);
    setEditForm({
      title: listing.title || '',
      description: listing.description || '',
      type: listing.type || '',
      category: listing.category || 'sale',
      price: listing.price || '',
      rooms: listing.rooms || '',
      area: listing.area || '',
      floor: listing.floor || '',
      total_floors: listing.total_floors || '',
      location: listing.location || '',
      address: listing.address || '',
      phone: listing.phone || '',
    });
    setEditModalOpen(true);
  };

  // Silmə modalını aç
  const openDeleteModal = (listing) => {
    setSelectedListing(listing);
    setDeleteModalOpen(true);
  };

  // Elanı yenilə
  const handleUpdateListing = async () => {
    if (!selectedListing?.id) return;

    try {
      setSaving(true);
      await listingService.updateFull(selectedListing.id, {
        ...editForm,
        images: selectedListing.images,
        amenities: selectedListing.amenities,
      });
      
      toast.success('Elan uğurla yeniləndi');
      setEditModalOpen(false);
      setSelectedListing(null);
      await loadData();
      if (onRefreshListings) onRefreshListings();
    } catch (error) {
      console.error('Elan yeniləmə xətası:', error);
      toast.error('Elanı yeniləmək mümkün olmadı');
    } finally {
      setSaving(false);
    }
  };

  // Elanı sil
  const handleDeleteListing = async () => {
    if (!selectedListing?.id) return;

    try {
      setSaving(true);
      await listingService.delete(selectedListing.id);
      
      toast.success('Elan uğurla silindi');
      setDeleteModalOpen(false);
      setSelectedListing(null);
      await loadData();
      if (onRefreshListings) onRefreshListings();
    } catch (error) {
      console.error('Elan silmə xətası:', error);
      toast.error('Elanı silmək mümkün olmadı');
    } finally {
      setSaving(false);
    }
  };

  const ListingCard = ({ listing }) => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="aspect-video bg-gray-100 relative">
        {listing.images?.[0] ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title || listing.type} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Image size={48} />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            listing.is_active ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {listing.is_active ? 'Aktiv' : 'Gözləmədə'}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1">{listing.title || listing.type || 'Elan'}</h3>
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
          <MapPin size={14} />
          <span className="truncate">{listing.location || 'Məkan göstərilməyib'}</span>
        </div>
        <div className="flex items-center gap-1 text-lg font-bold text-[#B91C1C] mb-3">
          <DollarSign size={18} />
          <span>{listing.price?.toLocaleString() || 0} AZN</span>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={() => openEditModal(listing)}
          >
            <Edit3 size={14} className="mr-1" /> Düzəliş et
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            asChild
          >
            <Link to={`/listings/${listing.id}`}>
              <Eye size={14} />
            </Link>
          </Button>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => openDeleteModal(listing)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/">
                <ArrowLeft size={18} className="mr-2" />
                Geri
              </Link>
            </Button>
            <span className="logo-text text-2xl font-semibold text-[#B91C1C]">Mydom</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Başlıq */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Building2 size={28} className="text-[#B91C1C]" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {userAgency?.name || 'Agentlik'} Kabineti
            </h1>
          </div>
          <p className="text-gray-600">Agentlik sahibi paneli</p>
        </div>

        {/* Statistika */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ListChecks size={18} />
              <span className="font-medium">Ümumi Elanlar</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{allListings.length}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ListChecks size={18} className="text-green-600" />
              <span className="font-medium">Aktiv Elanlar</span>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {allListings.filter(l => l.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Users size={18} className="text-blue-600" />
              <span className="font-medium">Əmlakçılar</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{agents.length}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Wallet size={18} className="text-purple-600" />
              <span className="font-medium">Balans</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Tezliklə</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="listings" className="flex items-center gap-2">
              <ListChecks size={16} />
              Bütün Elanlar
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Users size={16} />
              Əmlakçılar
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Building2 size={16} />
              Agentlik Ayarları
            </TabsTrigger>
          </TabsList>

          {/* Elanlar Tab */}
          <TabsContent value="listings">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B91C1C] mx-auto mb-4"></div>
                <p className="text-gray-500">Yüklənir...</p>
              </div>
            ) : allListings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Image size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Agentliyə aid heç bir elan yoxdur</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {allListings.map(listing => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Əmlakçılar Tab */}
          <TabsContent value="agents">
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Əmlakçılar Siyahısı</h3>
                <p className="text-sm text-gray-500">
                  Agentlik Kodu: <span className="font-mono font-bold">{userAgency?.access_code || 'N/A'}</span>
                </p>
              </div>
              
              {agents.length === 0 ? (
                <div className="text-center py-8">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">Hələ heç bir əmlakçı qoşulmayıb</p>
                  <p className="text-sm text-gray-400">
                    Əmlakçılar agentlik kodunuz ilə qeydiyyatdan keçə bilərlər
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {agents.map(agent => (
                    <div key={agent.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{agent.name || agent.email}</p>
                        <p className="text-sm text-gray-500">{agent.phone || 'Telefon yoxdur'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Elan sayı: {agent.listings_count || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Ayarlar Tab */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl border p-6 space-y-6">
              <h3 className="text-lg font-semibold">Agentlik Məlumatları</h3>
              
              {/* Loqo Bölməsi */}
              <div className="border-t pt-6">
                <Label className="text-gray-700 font-medium mb-4 block">Agentlik Loqosu</Label>
                <div className="flex items-center gap-6">
                  {/* Hazırkı Loqo */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                      {currentAgency?.logo || userAgency?.logo ? (
                        <img 
                          src={currentAgency?.logo || userAgency?.logo} 
                          alt={userAgency?.name || 'Agentlik'} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building2 size={32} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Loqo Yeniləmə Düyməsi */}
                  <div>
                    <Button
                      onClick={() => setLogoModalOpen(true)}
                      className="bg-[#B91C1C] hover:bg-[#991B1B]"
                    >
                      <Camera size={18} className="mr-2" />
                      Loqonu Yenilə
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Loqonuzu Cloudinary-yə yükləyərək yeniləyə bilərsiniz
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Digər Məlumatlar */}
              <div className="border-t pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-600">Agentlik Adı</Label>
                    <p className="font-medium mt-1">{userAgency?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Telefon</Label>
                    <p className="font-medium mt-1">{userAgency?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">E-poçt</Label>
                    <p className="font-medium mt-1">{userAgency?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Ünvan</Label>
                    <p className="font-medium mt-1">{userAgency?.address || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Agentlik Kodu</Label>
                    <p className="font-mono font-bold text-[#B91C1C] mt-1">{userAgency?.access_code || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <p className={`font-medium mt-1 ${userAgency?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {userAgency?.is_active ? 'Aktiv' : 'Deaktiv'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Düzəliş Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Elanı Düzəliş Et</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Başlıq</Label>
              <Input
                value={editForm.title || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Elan başlığı"
              />
            </div>
            <div className="space-y-2">
              <Label>Təsvir</Label>
              <Textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Elan təsviri"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kateqoriya</Label>
                <Select
                  value={editForm.category || 'sale'}
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Satış</SelectItem>
                    <SelectItem value="rent">Kirayə</SelectItem>
                    <SelectItem value="daily">Günlük</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Qiymət (AZN)</Label>
                <Input
                  type="number"
                  value={editForm.price || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Otaq Sayı</Label>
                <Input
                  type="number"
                  value={editForm.rooms || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, rooms: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Sahə (m²)</Label>
                <Input
                  type="number"
                  value={editForm.area || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, area: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mərtəbə</Label>
                <Input
                  type="number"
                  value={editForm.floor || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, floor: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Ümumi Mərtəbə</Label>
                <Input
                  type="number"
                  value={editForm.total_floors || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, total_floors: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Məkan</Label>
              <Input
                value={editForm.location || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Şəhər, rayon"
              />
            </div>
            <div className="space-y-2">
              <Label>Ünvan</Label>
              <Input
                value={editForm.address || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Tam ünvan"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={editForm.phone || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+994 XX XXX XX XX"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Ləğv et
            </Button>
            <Button 
              className="bg-[#B91C1C] hover:bg-[#991B1B]"
              onClick={handleUpdateListing}
              disabled={saving}
            >
              {saving ? 'Yüklənir...' : 'Yadda Saxla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loqo Yeniləmə Modalı */}
      <Dialog open={logoModalOpen} onOpenChange={handleCloseLogoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Loqonu Yenilə</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Hazırkı Loqo */}
            <div>
              <Label className="mb-2 block">Hazırkı Loqo</Label>
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center mx-auto">
                {currentAgency?.logo || userAgency?.logo ? (
                  <img 
                    src={currentAgency?.logo || userAgency?.logo} 
                    alt={userAgency?.name || 'Agentlik'} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Building2 size={48} className="text-gray-400" />
                )}
              </div>
            </div>

            {/* Yeni Loqo Seçimi */}
            <div className="space-y-2">
              <Label>Yeni Loqo Seç</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#B91C1C] transition-colors">
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoFileSelect}
                  className="hidden"
                  disabled={isUploadingLogo}
                />
                <label
                  htmlFor="logo-upload"
                  className={`cursor-pointer flex flex-col items-center ${isUploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {logoPreview ? (
                    <div className="space-y-4 w-full">
                      <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-[#B91C1C] mx-auto">
                        <img 
                          src={logoPreview} 
                          alt="Yeni loqo" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-sm text-green-600 font-medium">Yeni loqo seçildi</p>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="text-gray-400 mb-2" />
                      <p className="text-gray-600">Şəkil seçin</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG (maksimum 5MB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {logoFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Seçilmiş fayl:</strong> {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseLogoModal} disabled={isUploadingLogo}>
              Ləğv et
            </Button>
            <Button 
              className="bg-[#B91C1C] hover:bg-[#991B1B]"
              onClick={handleLogoUpload}
              disabled={isUploadingLogo || !logoFile}
            >
              {isUploadingLogo ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Yüklənir...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Yüklə
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silmə Təsdiq Modalı */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Elanı Sil</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Bu elanı silmək istədiyinizə əminsiniz? Bu əməliyyat geri qaytarıla bilməz.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Ləğv et
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteListing}
              disabled={saving}
            >
              {saving ? 'Silinir...' : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

